import {Exclude, Type} from 'class-transformer';
import {CONSTANTS, GLOBAL, IdCodeNameModel} from 'common-shared';
import {CostPriceComponentPriceModel} from "./cost-price-component-price.model";
import {PropertyMappingModel} from "../../properties/models/property-mapping.model";
import {UnitConversionModel} from '../../common/unit-conversions/models/unit-conversion.model';
import {MasterOperationModel} from "./master-operation.model";
import {ProcessSelectorModel} from "./process-selector.model";
import {SpecLineModel} from "../../properties/models/spec-line.model";
import {BaseConnectionModel} from "./base-connection.model";
import {DateTime} from "luxon";

export class ManufacturingItemModel extends IdCodeNameModel {
  @Type(() => CostPriceComponentPriceModel)
  CostPriceComponentPrices: CostPriceComponentPriceModel[] = [];
  @Type(() => PropertyMappingModel)
  ProcedureItemProperties: PropertyMappingModel[] = [];
  Images?: any;
  ItemTypeClv: string;
  ItemGroupId: string;
  TaxId: string;
  InventoryUnitId: string;
  StorageUnitId?: any;
  LotControlClv: string;
  IsPackage: boolean;
  OriginCountryId?: any;
  EanCode?: any;
  WeightUnitId: string;
  VolumeUnitId: string;
  SalesUnitId: string;
  PurchaseUnitId: string;
  ProductionUnitId: string;
  RouteUnitId: string;
  OutboundMethodClv: string;
  OrderSystemClv?: any;
  OrderMethodClv: string;
  OrderQuantityIncrement?: any;
  MinimumOrderQuantity?: any;
  MaximumOrderQuantity?: any;
  FixedOrderQuantity?: any;
  EconomicOrderQuantity?: any;
  ReorderPoint?: any;
  SafetyStock?: any;
  OrderIntervalDays?: any;
  OrderLeadTimeDays?: any;
  SafetyTimeDays?: any;
  IsPurchaseInspectionNeeded: boolean;
  AbcCode?: any;
  SlowMovingPercent?: any;
  ShelfLifePeriodClv?: any;
  ShelfLife: number;
  Comment?: any;
  @Type(() => UnitConversionModel)
  UnitConversions: UnitConversionModel[] = [];
  @Type(() => ItemInItemGroupModel)
  ItemInItemGroups: ItemInItemGroupModel[] = [];
  HierarchyNodes: any[];

  ObtainMethodClv: string;
  PurchaseLeadTime = 0;
  OperationSplitSizeMax?: number;
  AutoReplenishFlagClv: string;
  InventoryMin?: number;
  InventoryMax?: number;
  PurchaseLotSizeMin?: number;
  PurchaseLotSizeMax?: number;
  PurchaseLotSizeUnit?: number;
  ProductionLotSizeMin?: number;
  ProductionLotSizeMax?: number;
  ProductionLotSizeUnit?: number;
  RowVersion: string;
  ModifiedBy: string;
  @Type(() => Date)
  @Exclude({ toPlainOnly: true })
  ModifiedDate: Date;
  @Type(() => Date)
  @Exclude({ toPlainOnly: true })
  ModifiedDateForItemGroup: Date;

  @Type(() => MasterOperationModel)
  MasterOperations: MasterOperationModel[] = [];

  @Type(() => ProcessSelectorModel)
  ProcessSelectors: ProcessSelectorModel[] = [];
  SaveRouting = false;

  @Type(() => SpecLineModel)
  Specs: SpecLineModel[] = [];

  StatusClv: string;
  StatusCl: string;
  AssignedTo: string;
  ParentId: string;
  DraftId: string;
  OwnerClv: string;

  isManufactured() {
    return [GLOBAL.Classificators.ItemType.FinishedItem, GLOBAL.Classificators.ItemType.Phantom, GLOBAL.Classificators.ItemType.ManufacturedPart].find(value => value === this.ItemTypeClv);
  }

  get ProductionUnit()
  {
    return this.RouteUnitId ?? this.ProductionUnitId ?? this.InventoryUnitId;
  }

  isImeLoaded(): boolean {
    return this.MasterOperations?.length > 0;
  }

  isValid(): boolean {
    return this.Specs.every(spec => spec.isValid());
  }

  static CreateDefaultMaterial(init?: Partial<ManufacturingItemModel>) {
    return Object.assign(new ManufacturingItemModel(), {
      ObtainMethodClv: GLOBAL.Classificators.ObtainMethod.PreferToProduce,
      ItemTypeClv: GLOBAL.Classificators.ItemType.RawMaterial,
      InventoryUnitId: GLOBAL.Units.Kilogram,
      LotControlClv: GLOBAL.Classificators.LotControl.ByLot,
      WeightUnitId: GLOBAL.Units.Kilogram,
      VolumeUnitId: GLOBAL.Units.CubicM,
      SalesUnitId: GLOBAL.Units.Piece,
      PurchaseUnitId: GLOBAL.Units.Piece,
      OutboundMethodClv: GLOBAL.Classificators.OutboundMethod.NotRequired,
      OrderMethodClv: GLOBAL.Classificators.OrderMethod.PlannedOrder,
      OwnerClv: GLOBAL.Classificators.Owner.Ime
    }, init);
  }
  static CreateDefaultIntermediateItem(init?: Partial<ManufacturingItemModel>) {
    return Object.assign(new ManufacturingItemModel(), {
      ObtainMethodClv: GLOBAL.Classificators.ObtainMethod.PreferToProduce,
      ItemTypeClv: GLOBAL.Classificators.ItemType.IntermediateItem,
      InventoryUnitId: GLOBAL.Units.Kilogram,
      LotControlClv: GLOBAL.Classificators.LotControl.ByLot,
      WeightUnitId: GLOBAL.Units.Kilogram,
      VolumeUnitId: GLOBAL.Units.CubicM,
      SalesUnitId: GLOBAL.Units.Piece,
      PurchaseUnitId: GLOBAL.Units.Piece,
      OutboundMethodClv: GLOBAL.Classificators.OutboundMethod.NotRequired,
      OrderMethodClv: GLOBAL.Classificators.OrderMethod.PlannedOrder,
      OwnerClv: GLOBAL.Classificators.Owner.Ime
    }, init);
  }

  static CreateDefault(init?: Partial<ManufacturingItemModel>) {
    return Object.assign(new ManufacturingItemModel(), {
      ItemTypeClv: CONSTANTS.ItemType.ManufacturedPart,
      ObtainMethodClv: GLOBAL.Classificators.ObtainMethod.PreferToProduce,
      InventoryUnitId: GLOBAL.Units.Piece,
      LotControlClv: GLOBAL.Classificators.LotControl.ByLot,
      WeightUnitId: GLOBAL.Units.Kilogram,
      VolumeUnitId: GLOBAL.Units.CubicM,
      SalesUnitId: GLOBAL.Units.Piece,
      PurchaseUnitId: GLOBAL.Units.Piece,
      OutboundMethodClv: GLOBAL.Classificators.OutboundMethod.NotRequired,
      OrderMethodClv: GLOBAL.Classificators.OrderMethod.PlannedOrder,
      OwnerClv: GLOBAL.Classificators.Owner.Ime
    }, init);
  }
}

export class ItemInItemGroupModel extends BaseConnectionModel {
  constructor(init?: Partial<ItemInItemGroupModel>) {
    super();
    Object.assign(this, init);
  }

  ItemId: string;
  ItemGroupId: string;
  TypeClv: string;

  isActiveMain() {
    if (this.TypeClv !== CONSTANTS.ItemInItemGroupType.Main) {
      return false;
    }
    const now = DateTime.fromJSDate(new Date()).startOf('day');
    const from = DateTime.fromJSDate(this.FromDate ?? new Date()).startOf('day');
    const to = DateTime.fromJSDate(this.ToDate ?? new Date()).startOf('day');
    return from <= now && now <= to;
  }
}

